import React, { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, Badge } from '@/components/ui/Table';
import { db, collection, getDocs, query, orderBy, limit, doc, getDoc } from '@/lib/firebase';
import { formatDate } from '@/lib/firebase';
import type { AuditLog } from '@/types';
import { FileText, Activity, Filter, UserPlus, UserX, UserCheck, Key, Trash2 } from 'lucide-react';

interface AuditLogWithNames extends AuditLog {
  performedByName?: string;
  targetUserName?: string;
}

export const AuditLogsPage: React.FC = () => {
  const [logs, setLogs] = useState<AuditLogWithNames[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState<number | 'all'>(10);

  useEffect(() => {
    loadLogs();
  }, []);

  // Traduzione azioni in italiano
  const translateAction = (action: string): string => {
    const translations: Record<string, string> = {
      'USER_SUSPENDED': 'Utente Sospeso',
      'USER_REACTIVATED': 'Utente Riattivato',
      'USER_DELETED': 'Utente Eliminato',
      'USER_SELF_DELETED': 'Auto-Eliminazione Utente',
      'PASSWORD_SETUP_COMPLETED': 'Configurazione Password Completata',
      'USER_LOGIN': 'Accesso Utente',
      'USER_CREATED': 'Utente Creato',
      'USER_UPDATED': 'Utente Aggiornato',
    };
    return translations[action] || action.replace(/_/g, ' ');
  };

  // Recupera il nome utente dall'ID
  const getUserName = async (userId: string): Promise<string> => {
    if (!userId) return 'Sistema';
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData.displayName || userData.email || userId;
      }
      return userId;
    } catch (error) {
      console.error('Error fetching user name:', error);
      return userId;
    }
  };

  const loadLogs = async () => {
    try {
      setLoading(true);
      const logsQuery = query(
        collection(db, 'auditLogs'),
        orderBy('timestamp', 'desc'),
        limit(100)
      );
      const logsSnapshot = await getDocs(logsQuery);
      const logsData = logsSnapshot.docs.map(
        (doc) => ({ id: doc.id, ...doc.data() } as AuditLog)
      );

      // Carica i nomi degli utenti
      const logsWithNames = await Promise.all(
        logsData.map(async (log) => {
          const performedByName = await getUserName(log.performedBy);
          const targetUserName = log.targetUser ? await getUserName(log.targetUser) : undefined;
          return {
            ...log,
            performedByName,
            targetUserName,
          };
        })
      );

      setLogs(logsWithNames);
    } catch (error) {
      console.error('Error loading audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionBadgeVariant = (action: string) => {
    if (action.includes('CREATE') || action.includes('REACTIVATED') || action.includes('COMPLETED')) return 'success';
    if (action.includes('DELETE')) return 'danger';
    if (action.includes('SUSPEND')) return 'warning';
    if (action.includes('LOGIN')) return 'info';
    return 'default';
  };

  const getActionIcon = (action: string) => {
    if (action.includes('CREATE')) return <UserPlus className="w-4 h-4" />;
    if (action.includes('DELETE')) return <Trash2 className="w-4 h-4" />;
    if (action.includes('SUSPEND')) return <UserX className="w-4 h-4" />;
    if (action.includes('REACTIVATED')) return <UserCheck className="w-4 h-4" />;
    if (action.includes('PASSWORD') || action.includes('SETUP')) return <Key className="w-4 h-4" />;
    return <Activity className="w-4 h-4" />;
  };

  // Ottieni lista unica di azioni per il filtro
  const uniqueActions = Array.from(new Set(logs.map(log => log.action)));

  // Filtra i log
  const filteredLogs = filterAction === 'all'
    ? logs
    : logs.filter(log => log.action === filterAction);

  // Paginazione
  const totalPages = itemsPerPage === 'all' ? 1 : Math.ceil(filteredLogs.length / itemsPerPage);
  const paginatedLogs = itemsPerPage === 'all'
    ? filteredLogs
    : filteredLogs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Reset alla pagina 1 quando cambiano i filtri
  useEffect(() => {
    setCurrentPage(1);
  }, [filterAction, itemsPerPage]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Log Attività</h1>
              <p className="text-gray-600 mt-1">Storico delle azioni eseguite</p>
            </div>
          </div>

          {/* Filtro Azioni */}
          {uniqueActions.length > 0 && (
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-500" />
              <select
                value={filterAction}
                onChange={(e) => setFilterAction(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="all">Tutte le azioni ({logs.length})</option>
                {uniqueActions.map((action) => (
                  <option key={action} value={action}>
                    {translateAction(action)} ({logs.filter(l => l.action === action).length})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Logs Table */}
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  Registro Attività
                </CardTitle>
                <CardDescription>
                  Ultime 100 azioni eseguite dagli amministratori
                </CardDescription>
              </div>

              {/* Selettore items per pagina */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Mostra:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value="all">Tutti ({filteredLogs.length})</option>
                </select>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            {filteredLogs.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">
                  {logs.length === 0 ? 'Nessun log disponibile' : 'Nessun log trovato per questo filtro'}
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Azione</TableHead>
                        <TableHead>Eseguita da</TableHead>
                        <TableHead>Utente Target</TableHead>
                        <TableHead>Data/Ora</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedLogs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell>
                            <Badge variant={getActionBadgeVariant(log.action)} className="flex items-center gap-1.5 w-fit">
                              {getActionIcon(log.action)}
                              {translateAction(log.action)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm font-medium text-gray-900">
                              {log.performedByName || 'Sistema'}
                            </p>
                            <p className="text-xs text-gray-500 font-mono">
                              {log.performedBy?.substring(0, 8)}...
                            </p>
                          </TableCell>
                          <TableCell>
                            {log.targetUser ? (
                              <>
                                <p className="text-sm font-medium text-gray-900">
                                  {log.targetUserName || log.targetUser}
                                </p>
                                <p className="text-xs text-gray-500 font-mono">
                                  {log.targetUser.substring(0, 8)}...
                                </p>
                              </>
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-gray-600 whitespace-nowrap">
                            {formatDate(log.timestamp)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Paginazione */}
                {itemsPerPage !== 'all' && totalPages > 1 && (
                  <div className="flex items-center justify-between mt-6 pt-4 border-t">
                    <div className="text-sm text-gray-600">
                      Mostrando {((currentPage - 1) * (itemsPerPage as number)) + 1} - {Math.min(currentPage * (itemsPerPage as number), filteredLogs.length)} di {filteredLogs.length} record
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                      >
                        Precedente
                      </button>
                      <span className="text-sm text-gray-600">
                        Pagina {currentPage} di {totalPages}
                      </span>
                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                      >
                        Successiva
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};
